import React, { useEffect, useState, useCallback } from 'react';
import {
  patientMedicalHistoryService,
  medicalHistoryTypeService,
  type PatientMedicalHistory,
  type MedicalHistoryType,
} from '../../../services/patientMedicalHistoryService';
import EmptyState from '../../../components/ui/EmptyState';
import ConfirmModal from '../../../components/common/ConfirmModal';
import Spinner from '../../../components/ui/Spinner';
import { parseApiDate } from '../../../lib/format';

interface HistoryCategoryCardProps {
  patientId: number;
  category: MedicalHistoryType['category'];
  title: string;
  icon: string;
  /** Restrict to specific type codes within the category. Omit to allow all active types in the category. */
  includeTypeCodes?: string[];
}

const emptyForm = {
  history_type_id: 0,
  description: '',
  onset_date: '',
  notes: '',
};

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return '-';
  return (parseApiDate(dateString) ?? new Date(NaN)).toLocaleDateString('es-DO');
};

const HistoryCategoryCard: React.FC<HistoryCategoryCardProps> = ({ patientId, category, title, icon, includeTypeCodes }) => {
  const [items, setItems] = useState<PatientMedicalHistory[]>([]);
  const [types, setTypes] = useState<MedicalHistoryType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [histories, categoryTypes] = await Promise.all([
        patientMedicalHistoryService.getByPatient(patientId),
        medicalHistoryTypeService.getByCategory(category),
      ]);
      const scopedTypes = includeTypeCodes
        ? categoryTypes.filter(t => includeTypeCodes.includes(t.code))
        : categoryTypes;
      const typeIds = new Set(scopedTypes.map(t => t.id));
      const typesMap = new Map(scopedTypes.map(t => [t.id, t]));
      const scopedItems = histories
        .filter(h => typeIds.has(h.history_type_id))
        .map(h => ({ ...h, history_type: typesMap.get(h.history_type_id) || h.history_type }));
      setTypes(scopedTypes);
      setItems(scopedItems);
    } catch (err) {
      console.error(`Error fetching ${category} history:`, err);
    } finally {
      setLoading(false);
    }
  }, [patientId, category, includeTypeCodes]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.history_type_id || !form.description.trim()) {
      setError('Tipo y descripción son requeridos');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await patientMedicalHistoryService.create({
        patient_id: patientId,
        history_type_id: form.history_type_id,
        description: form.description,
        onset_date: form.onset_date || undefined,
        notes: form.notes || undefined,
      });
      setForm(emptyForm);
      setShowForm(false);
      void fetchAll();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar antecedente');
    } finally {
      setSaving(false);
    }
  };

  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (pendingDeleteId == null) return;
    setDeleting(true);
    const historyId = pendingDeleteId;
    setPendingDeleteId(null);
    try {
      await patientMedicalHistoryService.delete(historyId);
      void fetchAll();
    } catch (err) {
      console.error('Error deleting history:', err);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <Spinner variant="full-page" text={`Cargando ${title.toLowerCase()}...`} />;
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          <i className={`fa ${icon}`}></i> {title}
        </h3>
        <div className="card-tools">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setShowForm(!showForm);
              setForm(emptyForm);
              setError(null);
            }}
          >
            <i className={`fa ${showForm ? 'fa-times' : 'fa-plus'}`}></i>
            {showForm ? ' Cancelar' : ' Agregar'}
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && (
          <div className="alert alert-danger alert-sm mb-3">
            <i className="fa fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        {showForm && (
          <div className="card card-info mb-4" style={{ backgroundColor: '#f9f9f9' }}>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Tipo *</label>
                      <select
                        className="form-control"
                        value={form.history_type_id}
                        onChange={(e) => setForm(prev => ({ ...prev, history_type_id: parseInt(e.target.value) || 0 }))}
                        required
                      >
                        <option value={0}>Seleccionar tipo...</option>
                        {types.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Fecha de Inicio</label>
                      <input
                        type="date"
                        className="form-control"
                        value={form.onset_date}
                        onChange={(e) => setForm(prev => ({ ...prev, onset_date: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-8">
                    <div className="form-group">
                      <label>Descripción *</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={form.description}
                        onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Descripción del antecedente..."
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Notas</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={form.notes}
                        onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Observaciones adicionales..."
                      />
                    </div>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving ? <Spinner size="sm" text="Guardando..." /> : (<><i className="fa fa-save"></i> Guardar</>)}
                </button>
              </form>
            </div>
          </div>
        )}

        {items.length === 0 && !showForm ? (
          <EmptyState icon="fa-file-lines" title={`No tiene ${title.toLowerCase()} registrados`} className="py-4" />
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Descripción</th>
                  <th>Desde</th>
                  <th>Notas</th>
                  <th>Estado</th>
                  <th style={{ width: '80px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map(h => (
                  <tr key={h.id}>
                    <td><span className="badge bg-info-light">{h.history_type?.name || '-'}</span></td>
                    <td>{h.description}</td>
                    <td>{formatDate(h.onset_date)}</td>
                    <td><small className="text-muted">{h.notes || '-'}</small></td>
                    <td>
                      {h.is_active ? (
                        <span className="badge bg-success">Activo</span>
                      ) : (
                        <span className="badge bg-secondary">Inactivo</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-xs bg-danger-light"
                        title="Eliminar"
                        onClick={() => setPendingDeleteId(h.id)}
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        show={pendingDeleteId !== null}
        title="Eliminar antecedente"
        body="¿Está seguro que desea eliminar este antecedente? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        confirmVariant="danger"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleting) setPendingDeleteId(null); }}
      />
    </div>
  );
};

export default HistoryCategoryCard;
